import {useEffect, useRef} from "react";

export const ChromaKeyVideoPlayer = ({
  videoRef,
  isConnected,
  enableChromaKey = true,
  enableGreenKey = true,
  greenKeyMinG = 96,
  greenKeyRedRatio = 1.2,
  greenKeyBlueRatio = 1.15,
  greenKeyMinDelta = 18,
  greenKeyAlphaDenominator = 120,
  useBorderConnectedKey = false,
  blackKeyOnly = false,
  blackEdgeDespill = false,
  blackEdgeDespillStrength = 0.65,
  greenSpillDespill = false,
  greenSpillDespillStrength = 0.72,
  blackKeyHardLumaMax = 38,
  blackKeyHardChromaMax = 18,
  blackKeyLumaMax = 70,
  blackKeyChromaMax = 26,
  blueKeyColor = "#B6D6E6",
  blueKeyTolerance = 52,
  /** Добавка к радиусу ключа (тот же масштаб, что tolerance) — съедает кайму после сжатия видео */
  blueKeyFeather = 0,
  /**
   * Если > 0: на ярких пикселях (халат) ослабляем синий ключ, чтобы не «есть» ткань.
   * Порог — минимальная perceived luminance (0–255), выше него damp растёт к центру белого.
   */
  blueKeyProtectBrightFrom = 0,
  /**
   * Если > 0: средний RGB по верхним `n` строкам отрисованного кадра (чистый фон) как цвет ключа.
   * `blueKeyColor` остаётся запасным до первого кадра и если высины не хватает.
   */
  blueKeyAutoTopBandPx = 0,
  /** 0–1, сглаживание авто-ключа по кадрам (меньше мерцания) */
  blueKeyAutoSmooth = 0.12,
  /** Доп. ключ: B доминирует (Anam/Heygen часто не совпадают с hex) */
  blueKeyUseDominantBlue = false,
  keyedMinAlphaFactor = 0,
  wrapperClassName = "",
  playerClassName = "",
  videoId = undefined,
  videoProps = {},
  videoStyle = {},
  children = null,
}) => {
  const canvasRef = useRef(null);
  const chromaRafRef = useRef(null);
  const autoKeyRgbRef = useRef(null);

  useEffect(() => {
    if (!enableChromaKey) {
      return;
    }

    const canvasEl = canvasRef.current;

    if (!isConnected || !canvasEl) {
      autoKeyRgbRef.current = null;
      if (chromaRafRef.current) {
        cancelAnimationFrame(chromaRafRef.current);
        chromaRafRef.current = null;
      }
      return;
    }

    const ctx = canvasEl.getContext("2d", {willReadFrequently: true});
    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    if ("imageSmoothingQuality" in ctx) {
      ctx.imageSmoothingQuality = "high";
    }

    let isActive = true;

    const parseHexToRgb = (hex) => {
      if (typeof hex !== "string") return null;
      const normalized = hex.trim().replace(/^#/, "");
      if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;

      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
      };
    };

    const parsedBlueKeyColor = parseHexToRgb(blueKeyColor) ?? {
      r: 182,
      g: 214,
      b: 230,
    };
    const normalizedBlueTolerance = Number.isFinite(blueKeyTolerance)
      ? Math.max(0, blueKeyTolerance)
      : 52;
    const normalizedGreenKeyMinG = Number.isFinite(greenKeyMinG)
      ? Math.max(0, Math.min(255, greenKeyMinG))
      : 96;
    const normalizedGreenKeyRedRatio = Number.isFinite(greenKeyRedRatio)
      ? Math.max(1, greenKeyRedRatio)
      : 1.2;
    const normalizedGreenKeyBlueRatio = Number.isFinite(greenKeyBlueRatio)
      ? Math.max(1, greenKeyBlueRatio)
      : 1.15;
    const normalizedGreenKeyMinDelta = Number.isFinite(greenKeyMinDelta)
      ? Math.max(0, Math.min(255, greenKeyMinDelta))
      : 18;
    const normalizedGreenKeyAlphaDenominator = Number.isFinite(
      greenKeyAlphaDenominator,
    )
      ? Math.max(1, greenKeyAlphaDenominator)
      : 120;
    const normalizedFeather = Number.isFinite(blueKeyFeather)
      ? Math.max(0, blueKeyFeather)
      : 0;
    const blueKeyRadius = normalizedBlueTolerance + normalizedFeather;
    const blueKeyDistanceThresholdSq = blueKeyRadius ** 2;
    const protectBrightFrom = Number.isFinite(blueKeyProtectBrightFrom)
      ? Math.max(0, Math.min(255, blueKeyProtectBrightFrom))
      : 0;
    const protectBrightDenom = Math.max(1, 255 - protectBrightFrom);
    const autoTopBand = Math.max(0, Math.floor(blueKeyAutoTopBandPx));
    const autoSmoothBlend =
      Number.isFinite(blueKeyAutoSmooth) && blueKeyAutoSmooth > 0
        ? Math.min(1, blueKeyAutoSmooth)
        : 0.12;
    const normalizedKeyedMinAlphaFactor = Number.isFinite(keyedMinAlphaFactor)
      ? Math.max(0, Math.min(1, keyedMinAlphaFactor))
      : 0;
    const useBlackKeyOnly = Boolean(blackKeyOnly);
    const normalizedBlackKeyLumaMax = Number.isFinite(blackKeyLumaMax)
      ? Math.max(0, Math.min(255, blackKeyLumaMax))
      : 70;
    const normalizedBlackKeyChromaMax = Number.isFinite(blackKeyChromaMax)
      ? Math.max(0, Math.min(255, blackKeyChromaMax))
      : 26;
    const normalizedBlackKeyHardLumaMax = Number.isFinite(blackKeyHardLumaMax)
      ? Math.max(0, Math.min(255, blackKeyHardLumaMax))
      : 38;
    const normalizedBlackKeyHardChromaMax = Number.isFinite(blackKeyHardChromaMax)
      ? Math.max(0, Math.min(255, blackKeyHardChromaMax))
      : 18;
    const useBlackEdgeDespill = Boolean(blackEdgeDespill);
    const normalizedBlackEdgeDespillStrength = Number.isFinite(
      blackEdgeDespillStrength,
    )
      ? Math.max(0, Math.min(1, blackEdgeDespillStrength))
      : 0.65;
    const useGreenSpillDespill = Boolean(greenSpillDespill);
    const normalizedGreenSpillDespillStrength = Number.isFinite(
      greenSpillDespillStrength,
    )
      ? Math.max(0, Math.min(1, greenSpillDespillStrength))
      : 0.72;

    const resolveVideoEl = () => {
      const fromRef = videoRef?.current;
      if (
        fromRef?.readyState >= 2 &&
        fromRef.videoWidth > 0 &&
        fromRef.videoHeight > 0
      ) {
        return fromRef;
      }
      if (typeof videoId === "string" && videoId.length > 0) {
        const fromId = document.getElementById(videoId);
        if (
          fromId instanceof HTMLVideoElement &&
          fromId.readyState >= 2 &&
          fromId.videoWidth > 0 &&
          fromId.videoHeight > 0
        ) {
          return fromId;
        }
      }
      return fromRef ?? null;
    };

    const renderFrame = () => {
      if (!isActive) return;

      const activeVideoEl = resolveVideoEl();

      if (
        activeVideoEl &&
        activeVideoEl.readyState >= 2 &&
        activeVideoEl.videoWidth > 0 &&
        activeVideoEl.videoHeight > 0
      ) {
        const videoEl = activeVideoEl;
        const dpr = window.devicePixelRatio || 1;
        const displayWidth = Math.max(1, Math.round(canvasEl.clientWidth || 1));
        const displayHeight = Math.max(
          1,
          Math.round(canvasEl.clientHeight || 1),
        );
        const targetWidth = Math.round(displayWidth * dpr);
        const targetHeight = Math.round(displayHeight * dpr);

        if (
          canvasEl.width !== targetWidth ||
          canvasEl.height !== targetHeight
        ) {
          canvasEl.width = targetWidth;
          canvasEl.height = targetHeight;
        }

        const sourceWidth = videoEl.videoWidth;
        const sourceHeight = videoEl.videoHeight;
        const scale = Math.min(
          targetWidth / sourceWidth,
          targetHeight / sourceHeight,
        );
        const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
        const drawHeight = Math.max(1, Math.round(sourceHeight * scale));
        const drawX = Math.round((targetWidth - drawWidth) / 2);
        const drawY = Math.round(targetHeight - drawHeight);

        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(videoEl, drawX, drawY, drawWidth, drawHeight);

        const frame = ctx.getImageData(drawX, drawY, drawWidth, drawHeight);
        const {data} = frame;

        let keyR = parsedBlueKeyColor.r;
        let keyG = parsedBlueKeyColor.g;
        let keyB = parsedBlueKeyColor.b;

        if (autoTopBand > 0 && drawHeight >= autoTopBand && drawWidth > 0) {
          let sr = 0;
          let sg = 0;
          let sb = 0;
          let sc = 0;
          const yMax = Math.min(autoTopBand, drawHeight);
          for (let y = 0; y < yMax; y++) {
            const rowOffset = y * drawWidth * 4;
            for (let x = 0; x < drawWidth; x++) {
              const j = rowOffset + x * 4;
              sr += data[j];
              sg += data[j + 1];
              sb += data[j + 2];
              sc++;
            }
          }
          if (sc > 0) {
            const rawR = sr / sc;
            const rawG = sg / sc;
            const rawB = sb / sc;
            const prev = autoKeyRgbRef.current;
            if (!prev) {
              autoKeyRgbRef.current = {r: rawR, g: rawG, b: rawB};
            } else {
              const k = autoSmoothBlend;
              autoKeyRgbRef.current = {
                r: prev.r * (1 - k) + rawR * k,
                g: prev.g * (1 - k) + rawG * k,
                b: prev.b * (1 - k) + rawB * k,
              };
            }
            keyR = autoKeyRgbRef.current.r;
            keyG = autoKeyRgbRef.current.g;
            keyB = autoKeyRgbRef.current.b;
          }
        }

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          const lum = 0.299 * r + 0.587 * g + 0.114 * b;
          const chromaSpread = Math.max(r, g, b) - Math.min(r, g, b);
          /** Только почти нейтральные яркие пиксели (халат), не весь светлый blue-screen */
          const isProtectedFabric =
            protectBrightFrom > 0 &&
            lum >= protectBrightFrom &&
            chromaSpread < 28;

          const isGreen =
            !isProtectedFabric &&
            enableGreenKey &&
            g > normalizedGreenKeyMinG &&
            g > r * normalizedGreenKeyRedRatio &&
            g > b * normalizedGreenKeyBlueRatio &&
            g - Math.max(r, b) > normalizedGreenKeyMinDelta;
          const dr = r - keyR;
          const dg = g - keyG;
          const db = b - keyB;
          const blueDistanceSq = dr * dr + dg * dg + db * db;
          const isBlueDistanceKey =
            blueKeyDistanceThresholdSq > 0 &&
            blueDistanceSq <= blueKeyDistanceThresholdSq;
          const isDominantBlueBg =
            blueKeyUseDominantBlue &&
            b >= r + 10 &&
            b >= g - 6 &&
            lum >= 125 &&
            lum <= 252 &&
            chromaSpread >= 20;
          const isBlueKeyLike =
            !isProtectedFabric && (isBlueDistanceKey || isDominantBlueBg);

          if (isGreen || isBlueKeyLike) {
            const greenAlphaFactor = isGreen
              ? Math.min(
                  1,
                  (g - Math.max(r, b)) / normalizedGreenKeyAlphaDenominator,
                )
              : 0;
            let blueAlphaFactor = 0;
            if (isBlueDistanceKey) {
              blueAlphaFactor = Math.max(
                0,
                1 - blueDistanceSq / blueKeyDistanceThresholdSq,
              );
            } else if (isDominantBlueBg) {
              const blueLead = b - Math.max(r, g);
              blueAlphaFactor = Math.min(1, Math.max(0, blueLead / 48));
            }

            if (
              protectBrightFrom > 0 &&
              !isGreen &&
              blueAlphaFactor > 0
            ) {
              if (lum >= protectBrightFrom) {
                const t = Math.max(
                  0,
                  Math.min(1, (255 - lum) / protectBrightDenom),
                );
                blueAlphaFactor *= t * t;
              }
            }

            const alphaFactor = Math.max(
              greenAlphaFactor,
              blueAlphaFactor,
              normalizedKeyedMinAlphaFactor,
            );
            if (useGreenSpillDespill && alphaFactor > 0) {
              const greenSpill = Math.max(0, g - Math.max(r, b));
              const spillFactor = Math.max(0, Math.min(1, greenSpill / 96));
              const k =
                Math.max(alphaFactor, spillFactor) *
                normalizedGreenSpillDespillStrength;
              if (k > 0) {
                data[i + 1] = Math.round(g * (1 - k) + Math.max(r, b) * k);
              }
            }
            data[i + 3] = Math.max(
              0,
              Math.round(data[i + 3] * (1 - alphaFactor)),
            );
          }
        }

        ctx.putImageData(frame, drawX, drawY);
      }

      chromaRafRef.current = requestAnimationFrame(renderFrame);
    };

    renderFrame();

    return () => {
      isActive = false;
      autoKeyRgbRef.current = null;
      if (chromaRafRef.current) {
        cancelAnimationFrame(chromaRafRef.current);
        chromaRafRef.current = null;
      }
    };
  }, [
    blueKeyAutoSmooth,
    blueKeyAutoTopBandPx,
    blueKeyUseDominantBlue,
    blueKeyColor,
    greenKeyAlphaDenominator,
    greenKeyBlueRatio,
    greenKeyMinDelta,
    greenKeyMinG,
    greenKeyRedRatio,
    blueKeyFeather,
    blackKeyChromaMax,
    blackEdgeDespill,
    blackEdgeDespillStrength,
    greenSpillDespill,
    greenSpillDespillStrength,
    blackKeyHardChromaMax,
    blackKeyHardLumaMax,
    blackKeyLumaMax,
    blackKeyOnly,
    useBorderConnectedKey,
    blueKeyProtectBrightFrom,
    blueKeyTolerance,
    keyedMinAlphaFactor,
    enableGreenKey,
    enableChromaKey,
    isConnected,
    videoId,
    videoRef,
  ]);

  return (
    <div className={wrapperClassName}>
      <div className={playerClassName}>
        {enableChromaKey && (
          <canvas
            ref={canvasRef}
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: "100%",
              display: "block",
              pointerEvents: "none",
            }}
          />
        )}
        <video
          id={videoId}
          ref={videoRef}
          {...videoProps}
          style={{
            ...(enableChromaKey
              ? {
                  position: "absolute",
                  inset: 0,
                  zIndex: 0,
                  opacity: 0,
                  display: "none",
                  pointerEvents: "none",
                }
              : {}),
            ...videoStyle,
          }}
        >
          {children}
        </video>
      </div>
    </div>
  );
};
